/**
 * Optional cross-device mirroring for calendars.
 *
 * The default is still that events never leave the device (see
 * calendar/engine.ts and CLAUDE.md §3.6). But "local-only" has a real cost:
 * a calendar you can only authorise on one machine — a work account that
 * won't sign in on a personal laptop, say — is then invisible everywhere
 * else, and there is nothing the user can do about it.
 *
 * So mirroring exists, and it is **opt-in per account**. Turn it on and that
 * account's cached events are written to the user's OWN cloud storage (the
 * same GitHub repo / Drive folder everything else lives in) through the
 * provider-agnostic raw-file API, so both providers behave identically
 * (§3.5). Other devices read it and show those calendars read-only.
 *
 * Two things keep this honest:
 *  - It is never automatic. Nothing is uploaded unless the user turns it on
 *    for that specific account, with the consequence spelled out in the UI.
 *  - It respects the calendar's privacy setting. Pair it with "Hide event
 *    titles" and what crosses is busy time and nothing else — which is the
 *    combination to recommend for a work calendar.
 */
export const CALENDAR_SHARED_FILE = 'calendar-shared.json';
/**
 * A narrower window than the local cache keeps the file small: this is a
 * convenience mirror, not an archive, and it rides in the user's data repo —
 * every sync reads and writes it.
 *
 * Three weeks forward covers what a mirrored calendar is actually used for:
 * today's plan, this week, and enough of the next to notice what's coming.
 * Looking further ahead is a job for the device that owns the account, which
 * keeps 120 days locally. A week back is kept for reviews.
 *
 * (Recurring meetings are already expanded into individual events by Google's
 * API — `singleEvents` — so a weekly stand-up inside this window is 3 entries,
 * not a rule to evaluate.)
 */
export const SHARED_DAYS_BACK = 7;
export const SHARED_DAYS_FORWARD = 21;
function emptyFile() {
    return { version: 1, accounts: {} };
}
/**
 * One answer per account, not one per device.
 *
 * Two devices can both hold the credentials for the same account — a personal
 * Gmail on a laptop and a phone, say — and each keeps its own account record.
 * If they disagreed about sharing titles they would take turns publishing,
 * and a third device reading the mirror would watch meeting names appear and
 * vanish depending on which one synced last. Unmanageable, and not a setting
 * anyone should have to keep in step by hand.
 *
 * So the choice lives in the shared file and a routine publish **adopts**
 * whatever is already there. Changing the switch is explicitly authoritative
 * (`authoritative: true`), which is what lets a deliberate change propagate
 * instead of being pulled back by the next device to sync. The result is one
 * value that converges, with the user's most recent instruction winning.
 */
export function resolveSharePrivacy({ local, published, authoritative = false, }) {
    if (authoritative)
        return local ?? 'busy';
    return published ?? local ?? 'busy';
}
/**
 * Read the shared file.
 *
 * Returns `null` when the file could not be read or could not be parsed —
 * NOT an empty file. The distinction matters enormously: callers prune
 * mirrored calendars that have disappeared from this file, so treating "the
 * network is down" as "nobody is sharing anything" would delete a mirror the
 * moment a request failed. That is the exact failure users fear — the data
 * they pulled yesterday vanishing because today's fetch didn't land.
 *
 * A genuinely absent file (the provider reports no such path) is an empty
 * file, because that really does mean nothing is being shared.
 */
export async function readSharedFile(read) {
    let raw;
    try {
        raw = await read(CALENDAR_SHARED_FILE);
    }
    catch {
        return null;
    }
    if (raw === null || raw === undefined)
        return emptyFile();
    if (raw.trim() === '')
        return emptyFile();
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || typeof parsed.accounts !== 'object') {
            return null;
        }
        return { version: 1, accounts: parsed.accounts ?? {} };
    }
    catch {
        return null;
    }
}
/**
 * Replace only the slices this device owns, leaving every other device's
 * contribution untouched.
 *
 * This read-modify-write is what lets a work laptop publish the office
 * account while a personal laptop publishes two personal ones, into the same
 * file, without either clobbering the other. Two devices writing in the same
 * instant could still lose one slice; the next refresh restores it, which is
 * the right trade for a cache.
 */
export function mergeOwnedSlices(existing, ownedAccountIds, slices) {
    const accounts = { ...existing.accounts };
    // Drop slices for accounts this device owns but is no longer sharing —
    // turning sharing off has to actually remove the data, not just stop
    // updating it.
    for (const id of ownedAccountIds) {
        if (!(id in slices))
            delete accounts[id];
    }
    for (const [id, slice] of Object.entries(slices)) {
        accounts[id] = slice;
    }
    return { version: 1, accounts };
}
/** Trim an account's cached events to the shared window. */
export function eventsForSharing(events, today = new Date()) {
    const key = (offset) => {
        const d = new Date(today);
        d.setDate(d.getDate() + offset);
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };
    const from = key(-SHARED_DAYS_BACK);
    const to = key(SHARED_DAYS_FORWARD);
    return events.filter((e) => e.date >= from && e.date <= to);
}
//# sourceMappingURL=shared-file.js.map